// @flow
import React from "react";
import classNames from "classnames";
import { withGlobalContext } from "../../common/GlobalContext";
import withStyles from "elevate-ui/withStyles";
import Typography from "elevate-ui/Typography";
import Hr from "elevate-ui/Hr";

import type { $Agent, $SiteOffice } from "flow/types";

type $Props = {
  agent: $Agent,
  classes: Object,
  isEqualHousingOpportunity: boolean,
  tenantConfig: Object,
  office: $SiteOffice,
  privacyPolicy: { href: string, label: string },
  accessibility: { href: string, label: string },
};

const Copyright = ({
  agent,
  classes,
  isEqualHousingOpportunity,
  tenantConfig,
  office,
  privacyPolicy,
  accessibility,
}: $Props) => {
  const ownerName = agent
    ? `${agent.firstName} ${agent.lastName}`
    : office
      ? office.name
      : "";
  return (
    <div className={classes.root}>
      <Hr className={classes.divider} />
      {tenantConfig &&
        tenantConfig.copyright &&
        tenantConfig.copyright.showOwnerName &&
        tenantConfig.copyright.url && (
          <Typography type="body" className={classes.text}>
            <a
              href={tenantConfig.copyright.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              Copyright &copy;{" "}
              {tenantConfig.copyright.showOwnerName
                ? ownerName
                : "Elm Street Technology, LLC."}
              <br />
              All rights Reserved
            </a>
          </Typography>
        )}
      {isEqualHousingOpportunity && (
        <div className={classNames(classes.eho, classes.marginBottom)}>
          <Typography type="body" className={classes.text}>
            <img
              src="/images/fheo.png"
              alt="Equal Housing Opportunity"
              className={classes.ehoImage}
              width="28px"
              height="28px"
            />
            <a href="//www.hud.gov" target="_blank" rel="noopener noreferrer">
              Equal Housing Opportunity
            </a>
          </Typography>
        </div>
      )}
      <div className={classes.eho}>
        <Typography className={classes.links} gutterTop>
          <a href="/terms" target="_blank" rel="noopener noreferrer">
            Terms of Service
          </a>
          {privacyPolicy &&
            privacyPolicy.href && (
              <a
                href={privacyPolicy.href}
                target="_blank"
                rel="noopener noreferrer"
              >
                {privacyPolicy.label}
              </a>
            )}
          {tenantConfig &&
            tenantConfig.copyright &&
            tenantConfig.copyright.showDMCANotice && (
              <a href="/dmca" target="_blank" rel="noopener noreferrer">
                DMCA Notice
              </a>
            )}
        </Typography>
      </div>
      <div className={classes.eho}>
        <Typography className={classes.links} gutterTop>
          {accessibility &&
            accessibility.href && (
              <a
                href={accessibility.href}
                target="_blank"
                rel="noopener noreferrer"
              >
                {accessibility.label}
              </a>
            )}
        </Typography>
      </div>
    </div>
  );
};

const styles = (theme) => {
  return {
    eho: {
      display: "flex",
      position: "relative",
      flexFlow: "row nowrap",
      justifyContent: "center",
      alignItems: "center",
      "& img": {
        marginRight: theme.spacing.unit * 0.5,
      },
    },
    marginBottom: { marginBottom: "10px" },
    ehoImage: { verticalAlign: "middle", paddingRight: "6px" },
    links: {
      "& a": {
        position: "relative",
        paddingRight: theme.spacing.unit,
        "&:after": {
          display: "block",
          position: "absolute",
          right: "4px",
          top: "0",
          content: "'|'",
        },
        "&:last-of-type": {
          paddingRight: "0",
          "&:after": {
            display: "none",
          },
        },
      },
    },
    divider: {
      backgroundColor: theme.colors.primaryFontColor,
      [theme.breakpoints(628)]: {
        display: "none",
      },
    },
    text: {
      color: theme.colors.primaryFontColor,
      fontSize: "14px",
      lineHeight: "18px",
      marginBottom: 0,
    },
  };
};

export default withGlobalContext(withStyles(styles)(Copyright));
